/**
 * Form Validator
 * Functions for validating forms and individual fields
 */

/**
 * Validate the entire form
 * @param {Object} formData - Form data object
 * @param {Array} formFields - Array of field configurations
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const validateForm = (formData, formFields) => {
  const errors = {};

  formFields.forEach((field) => {
    const value = formData[field.name];

    // Skip validation for profilePicture
    if (field.name === 'profilePicture') return;

    // Required field validation
    if (
      field.required &&
      (value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0))
    ) {
      errors[field.name] = `${field.label || field.name} is required`;
    }

    // Email format validation
    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errors[field.name] = 'Please enter a valid email';
      }
    }

    // Phone format validation
    if (field.type === 'phone' && value) {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
      if (!phoneRegex.test(value)) {
        errors[field.name] = 'Please enter a valid phone number';
      }
    }

    // Custom validator
    if (field.validate && typeof field.validate === 'function') {
      const customError = field.validate(value, formData);
      if (customError) {
        errors[field.name] = customError;
      }
    }
  });

  // Manufacturing Date vs Expiry Date validation
  if (formData.manufacturingDate && formData.expiryDate) {
    const mfgDate = new Date(formData.manufacturingDate);
    const expDate = new Date(formData.expiryDate);

    if (mfgDate > expDate) {
      errors.expiryDate = 'Expiry Date must be later than Manufacturing Date';
    }
  }

  return errors;
};

/**
 * Validate individual field on change
 * @param {string} name - Field name
 * @param {any} value - Field value
 * @param {Array} formFields - Array of field configurations
 * @param {Object} formData - Full form data (for cross-field validation)
 * @returns {string} - Error message or empty string
 */
export const validateField = (name, value, formFields, formData = {}) => {
  const field = formFields.find((f) => f.name === name);
  if (!field) return '';

  // Skip validation for profilePicture
  if (field.name === 'profilePicture') return '';

  // Required field validation
  if (
    field.required &&
    (value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0))
  ) {
    return `${field.label || field.name} is required`;
  }

  // Email format validation
  if (field.type === 'email' && value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email';
  }

  // Phone format validation
  if (field.type === 'phone' && value) {
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
    if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
  }

  // Custom validator
  if (field.validate && typeof field.validate === 'function') {
    const customError = field.validate(value, formData);
    if (customError) return customError;
  }

  // Field-level Manufacturing vs Expiry validation
  if (name === 'expiryDate' && formData?.manufacturingDate && value) {
    const mfgDate = new Date(formData.manufacturingDate);
    const expDate = new Date(value);
    if (mfgDate > expDate) {
      return 'Expiry Date must be later than Manufacturing Date';
    }
  }

  return '';
};

/**
 * Check if form has any errors
 * @param {Object} errors - Errors object
 * @returns {boolean} - True if form has errors
 */
export const hasErrors = (errors) => {
  return Object.values(errors).some((error) => error !== '');
};

/**
 * Get first error message from errors object
 * @param {Object} errors - Errors object
 * @returns {string} - First error message or empty string
 */
export const getFirstError = (errors) => {
  const firstError = Object.values(errors).find((error) => error !== '');
  return firstError || '';
};

export default {
  validateForm,
  validateField,
  hasErrors,
  getFirstError,
};
